import React, { useEffect, useRef, useState } from 'react';
import { cn } from "@/lib/utils";
import { openExternalLink } from '@/lib/navigation';
import { BookMarkedIcon, FileDownIcon, SearchIcon, Trash2Icon, ChevronRightIcon } from "lucide-react";
import { useAdminPerms } from '@/hooks/auth';
import { useLiveConsoleHistory } from '@/pages/LiveConsole/liveConsoleHooks';
import { useAtomValue } from 'jotai';
import { fxRunnerStateAtom } from '@/hooks/status';

type ConsoleFooterButtonProps = {
    icon: React.ElementType;
    title: string;
    disabled?: boolean;
    onClick: () => void;
}

function ConsoleFooterButton({ icon: Icon, title, disabled, onClick }: ConsoleFooterButtonProps) {
    return (
        <button
            tabIndex={0}
            disabled={disabled}
            onClick={() => !disabled && onClick()}
            onKeyDown={(e) => (e.code === 'Enter' || e.code === 'Space') && !disabled && onClick()}
            title={title}
            className={cn(
                'group flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all duration-150',
                'text-[11px] font-semibold uppercase tracking-wider font-mono',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00ffd1]/40',
                disabled
                    ? 'opacity-30 cursor-not-allowed border-white/[0.04] text-slate-600 bg-transparent'
                    : 'border-white/[0.06] text-slate-500 bg-white/[0.02] hover:border-[#00ffd1]/25 hover:text-[#00ffd1] hover:bg-[#00ffd1]/5 cursor-pointer'
            )}
        >
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden 2xl:inline">{title}</span>
        </button>
    );
}

type LiveConsoleFooterProps = {
    isConnected: boolean;
    consoleWrite: (_data: string) => void;
    consoleClear: () => void;
    toggleSaveSheet: () => void;
    toggleSearchBar: () => void;
    termInputRef: React.RefObject<HTMLInputElement>;
}

export default function LiveConsoleFooter(props: LiveConsoleFooterProps) {
    const { history, appendHistory } = useLiveConsoleHistory();
    const [histIndex, setHistIndex] = useState(-1);
    const savedInput = useRef('');
    const termInputRef = props.termInputRef;
    const { hasPerm } = useAdminPerms();
    const hasWritePerm = hasPerm('console.write');
    const fxRunnerState = useAtomValue(fxRunnerStateAtom);

    useEffect(() => {
        if (props.isConnected && termInputRef.current) {
            termInputRef.current.focus();
        }
    }, [props.isConnected, termInputRef]);

    const handleArrowUp = () => {
        if (!termInputRef.current) return;
        if (histIndex === -1) savedInput.current = termInputRef.current.value ?? '';
        const nextHistId = histIndex + 1;
        if (history[nextHistId]) {
            termInputRef.current.value = history[nextHistId];
            setHistIndex(nextHistId);
        }
    };

    const handleArrowDown = () => {
        if (!termInputRef.current) return;
        const prevHistId = histIndex - 1;
        if (prevHistId === -1) {
            termInputRef.current.value = savedInput.current;
            setHistIndex(prevHistId);
        } else if (history[prevHistId]) {
            termInputRef.current.value = history[prevHistId];
            setHistIndex(prevHistId);
        }
    };

    const handleEnter = () => {
        if (!termInputRef.current) return;
        const currentInput = termInputRef.current.value.trim();
        setHistIndex(-1);
        termInputRef.current.value = '';
        savedInput.current = '';
        if (currentInput) {
            appendHistory(currentInput);
            props.consoleWrite(currentInput);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!props.isConnected) return;
        if (e.key === 'ArrowUp') { handleArrowUp(); e.preventDefault(); }
        else if (e.key === 'ArrowDown') { handleArrowDown(); e.preventDefault(); }
        else if (e.key === 'Enter') { handleEnter(); e.preventDefault(); }
    };

    let inputError: string | undefined;
    if (!hasWritePerm) {
        inputError = 'No console.write permission.';
    } else if (!fxRunnerState.isChildAlive) {
        inputError = 'Server is not running.';
    } else if (!props.isConnected) {
        inputError = 'Socket disconnected.';
    }

    return (
        <div className="flex flex-col xs:flex-row xs:items-center gap-2 px-3 py-2.5 border-t border-white/[0.05] bg-[#030507]/95 shrink-0">
            {/* Command input */}
            <div className="flex items-center gap-2 grow min-w-0">
                <ChevronRightIcon
                    size={14}
                    className={cn(
                        "shrink-0 transition-colors",
                        inputError ? "text-destructive/60" : "text-[#00ffd1]/60"
                    )}
                />
                <input
                    ref={termInputRef}
                    type="text"
                    disabled={!!inputError}
                    placeholder={inputError ?? "Type a command and press Enter..."}
                    autoCapitalize="none"
                    autoComplete="off"
                    autoCorrect="off"
                    onKeyDown={handleInputKeyDown}
                    className={cn(
                        "w-full bg-white/[0.02] border rounded-lg px-3 py-1.5",
                        "font-mono text-xs text-slate-100 placeholder:text-slate-600",
                        "focus:outline-none transition-all duration-150",
                        "disabled:opacity-40 disabled:cursor-not-allowed",
                        inputError
                            ? "border-destructive/20 focus:border-destructive/40 focus:ring-1 focus:ring-destructive/20"
                            : "border-white/[0.06] focus:border-[#00ffd1]/30 focus:ring-1 focus:ring-[#00ffd1]/15 focus:bg-white/[0.03]"
                    )}
                />
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
                <ConsoleFooterButton
                    icon={BookMarkedIcon}
                    title="Saved"
                    onClick={props.toggleSaveSheet}
                />
                <ConsoleFooterButton
                    icon={SearchIcon}
                    title="Search"
                    disabled={!props.isConnected}
                    onClick={props.toggleSearchBar}
                />
                <ConsoleFooterButton
                    icon={Trash2Icon}
                    title="Clear"
                    disabled={!props.isConnected}
                    onClick={props.consoleClear}
                />
                <ConsoleFooterButton
                    icon={FileDownIcon}
                    title="Download"
                    disabled={!props.isConnected}
                    onClick={() => openExternalLink('/fxserver/downloadLog')}
                />
            </div>
        </div>
    );
}
